'use client';

/**
 * The one slider in the product.
 *
 * Before this existed, `FreqRangeSlider` / `SpeakerDistanceRange` /
 * `ToolDimSlider` each hand-rolled their own thumb + drag math, and every one
 * of them only responded to dragging a ~13px thumb — clicking directly on
 * the track did nothing. This is the fix: clicking anywhere on the track
 * jumps the nearest thumb there, dragging still works, and the value readout
 * next to the track is click-to-edit (type an exact number, Enter/blur
 * commits, Escape reverts).
 *
 * The value<->position mapping is injectable (`valueToPct`/`pctToValue`) so
 * callers can plug in whatever scale they need — linear by default,
 * log-frequency for `FreqRangeSlider`. `snap` runs after every pointer/
 * keyboard/typed change (e.g. third-octave snapping); it does NOT enforce
 * min-separation between two thumbs in range mode — that's business logic
 * specific to each caller's `onChange`, not something this primitive can
 * know in a mapping-agnostic way.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FOCUS_RING } from './focusRing';

function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

function defaultParse(raw: string): number | null {
  const n = parseFloat(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Vertical padding around the 3px rail — matches native `<input type="range">`
 *  hit targets so click-to-jump works on the visible thumb band, not only on
 *  a 3px sliver (RoomPanel's Length/Width/Height sliders use native range and
 *  felt fine for this reason). */
const TRACK_HIT_PAD_Y_PX = 8;

export interface SliderValueFieldProps {
  value: number;
  onCommit: (v: number) => void;
  format?: (v: number) => string;
  parse?: (raw: string) => number | null;
  min?: number;
  max?: number;
  ariaLabel?: string;
  testId?: string;
  className?: string;
  /**
   * How the value sits at rest.
   *
   * `'readout'` (the default) is the quiet number beside a slider: a button
   * until clicked, then a text input until committed.
   *
   * `'field'` is always the text input, for a surface that presents the
   * value as something to type into: the editor's `ed-input` well
   * (room/shared/DimensionField). The commit rules are the same in both —
   * Enter or blur commits, Escape reverts, never a keystroke — so a
   * multi-digit entry cannot be clamped away one digit at a time. Focusing
   * and leaving the field without typing commits nothing, so a stored value
   * more precise than `format` shows is never re-rounded by a stray click.
   */
  presentation?: 'readout' | 'field';
}

/**
 * A number you can click and type into, committed on Enter or blur. At rest
 * it is either a button (the default readout inside `Slider`) or an always-on
 * text field — see `presentation`. Exported standalone so a caller composing
 * a custom header (see `FreqRangeSlider`, `DimensionField`) can reuse the
 * same commit behaviour outside the primitive's own layout.
 */
export function SliderValueField({
  value,
  onCommit,
  format = (v) => String(v),
  parse = defaultParse,
  min,
  max,
  ariaLabel,
  testId,
  className,
  presentation = 'readout',
}: SliderValueFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);
  // A ref, not state: Escape calls .blur() in the same synchronous handler
  // that sets it, and the blur handler must see it immediately — a state
  // update wouldn't be visible until the next render, by which point the
  // (soon-to-be-stale) onBlur closure has already read the wrong value.
  const cancelledRef = useRef(false);
  // Field presentation only, and refs for the same reason as `cancelledRef`:
  // Enter commits and then blurs, and the blur handler runs before React
  // re-renders, so it must see "already committed" synchronously or the
  // value commits twice. `typedRef` is "the text differs from the stored
  // value because someone typed it".
  const typedRef = useRef(false);
  // Focus selects the whole value so click-then-type replaces it, as the
  // readout's click-to-edit does. The mouseup ending that same click would
  // otherwise drop a caret where the pointer landed and throw the selection
  // away, so it is swallowed once.
  const keepSelectionRef = useRef(false);

  const commit = (raw: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      setDraft(null);
      return;
    }
    const n = parse(raw);
    if (n === null) {
      setDraft(null);
      return;
    }
    const clamped = min !== undefined && max !== undefined ? clampNum(n, min, max) : n;
    onCommit(clamped);
    setDraft(null);
  };

  if (presentation === 'field') {
    // Nothing typed, nothing to commit: a click in and out never re-rounds.
    const commitTyped = (raw: string) => {
      if (!typedRef.current) return;
      typedRef.current = false;
      commit(raw);
    };
    return (
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        data-testid={testId}
        value={draft ?? format(value)}
        onChange={(e) => {
          typedRef.current = true;
          setDraft(e.target.value);
        }}
        onFocus={(e) => {
          e.currentTarget.select();
          keepSelectionRef.current = true;
        }}
        onMouseUp={(e) => {
          if (keepSelectionRef.current) e.preventDefault();
          keepSelectionRef.current = false;
        }}
        onBlur={(e) => {
          keepSelectionRef.current = false;
          commitTyped(e.currentTarget.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commitTyped(e.currentTarget.value);
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            typedRef.current = false;
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
        className={className ?? 'ed-input ed-mono text-right'}
      />
    );
  }

  if (draft === null) {
    return (
      <button
        type="button"
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={() => setDraft(format(value))}
        className={
          className ?? 'ed-mono text-body-sm tabular-nums text-[var(--ed-text-dim)] hover:text-[var(--ed-text)]'
        }
      >
        {format(value)}
      </button>
    );
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      autoFocus
      aria-label={ariaLabel}
      data-testid={testId}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          cancelledRef.current = true;
          e.currentTarget.blur();
        }
      }}
      className={
        className ??
        'ed-mono text-body-sm tabular-nums w-16 border-b border-[var(--ed-accent)] bg-transparent text-right text-[var(--ed-text-dim)] outline-none'
      }
    />
  );
}

export interface SliderThumb {
  value: number;
  onChange: (v: number) => void;
  ariaLabel?: string;
  testId?: string;
  format?: (v: number) => string;
  parse?: (raw: string) => number | null;
}

export interface SliderProps {
  min: number;
  max: number;
  /** Keyboard arrow-key increment. Also the default linear mapping's grain
   *  has no relation to this — `snap` is the thing that quantizes values. */
  step?: number;
  valueToPct?: (v: number) => number;
  pctToValue?: (pct: number) => number;
  /** Runs on pointer and keyboard changes before `onChange` fires — never on
   *  typed readout commits (those only clamp to `[min, max]`). */
  snap?: (v: number) => number;
  /** One thumb for a plain slider, two for a range slider. */
  thumbs: SliderThumb[];
  color?: string;
  testId?: string;
  label?: ReactNode;
  /** Renders the built-in click-to-edit readout row. Default true. */
  editable?: boolean;
  /** Replaces the default readout entirely (still gets click-to-edit via
   *  `SliderValueField` if the caller composes it that way). */
  renderReadout?: (thumbs: SliderThumb[]) => ReactNode;
  /** Value-space positions to draw as static ticks on the track. */
  ticks?: number[];
  className?: string;
  disabled?: boolean;
}

export default function Slider({
  min,
  max,
  step = 1,
  valueToPct,
  pctToValue,
  snap,
  thumbs,
  color = 'var(--ed-accent)',
  testId,
  label,
  editable = true,
  renderReadout,
  ticks,
  className,
  disabled = false,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const toPct = valueToPct ?? ((v: number) => ((v - min) / (max - min)) * 100);
  const fromPct = pctToValue ?? ((pct: number) => min + (clampNum(pct, 0, 100) / 100) * (max - min));

  // Cached so the document-bound move handler sees fresh thumbs/callbacks
  // without re-binding listeners on every render.
  const stateRef = useRef({ thumbs, toPct, fromPct, snap });
  useEffect(() => {
    stateRef.current = { thumbs, toPct, fromPct, snap };
  });

  const valueFromClientX = useCallback((clientX: number): number | null => {
    const track = trackRef.current;
    if (!track) return null;
    const r = track.getBoundingClientRect();
    if (r.width <= 0) return null;
    const pct = ((clientX - r.left) / r.width) * 100;
    let v = stateRef.current.fromPct(clampNum(pct, 0, 100));
    if (stateRef.current.snap) v = stateRef.current.snap(v);
    return v;
  }, []);

  const nearestThumbIdx = useCallback((v: number): number => {
    const { thumbs: t, toPct: p } = stateRef.current;
    if (t.length < 2) return 0;
    const d0 = Math.abs(p(v) - p(t[0]!.value));
    const d1 = Math.abs(p(v) - p(t[1]!.value));
    return d0 <= d1 ? 0 : 1;
  }, []);

  const onMove = useCallback((e: PointerEvent) => {
    if (activeIdx === null) return;
    const v = valueFromClientX(e.clientX);
    if (v === null) return;
    stateRef.current.thumbs[activeIdx]?.onChange(v);
  }, [activeIdx, valueFromClientX]);

  const onUp = useCallback(() => setActiveIdx(null), []);

  useEffect(() => {
    if (activeIdx === null) return;
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [activeIdx, onMove, onUp]);

  const onTrackPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    // Thumbs stopPropagation, but guard anyway when the event target is a thumb.
    if ((e.target as Element).closest?.('[role="slider"]')) return;
    const v = valueFromClientX(e.clientX);
    if (v === null) return;
    const idx = nearestThumbIdx(v);
    thumbs[idx]?.onChange(v);
    setActiveIdx(idx);
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* jsdom may not implement capture */
    }
  };

  const onThumbPointerDown = (idx: number) => (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setActiveIdx(idx);
  };

  const onThumbKeyDown = (idx: number) => (e: React.KeyboardEvent) => {
    if (disabled) return;
    const t = thumbs[idx];
    if (!t) return;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = t.value + step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = t.value - step;
    else if (e.key === 'Home') next = min;
    else if (e.key === 'End') next = max;
    if (next === null) return;
    e.preventDefault();
    let v = clampNum(next, min, max);
    if (snap) v = snap(v);
    t.onChange(v);
  };

  const pcts = thumbs.map((t) => clampNum(toPct(t.value), 0, 100));
  const range = thumbs.length >= 2;

  return (
    <div data-testid={testId} className={className ?? 'flex flex-col gap-1.5'}>
      {(label !== undefined || (editable && thumbs.length > 0)) && (
        <div className="flex items-center justify-between gap-2">
          {label !== undefined && (
            <span className="text-body-sm text-[var(--ed-text-dim)]">{label}</span>
          )}
          {editable &&
            (renderReadout ? (
              renderReadout(thumbs)
            ) : (
              <span className="flex items-center gap-1">
                {thumbs.map((t, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 && <span className="ed-caption">–</span>}
                    <SliderValueField
                      value={t.value}
                      onCommit={(v) => {
                        t.onChange(clampNum(v, min, max));
                      }}
                      format={t.format}
                      parse={t.parse}
                      min={min}
                      max={max}
                      ariaLabel={t.ariaLabel}
                      testId={t.testId ? `${t.testId}-value` : undefined}
                    />
                  </span>
                ))}
              </span>
            ))}
        </div>
      )}
      <div
        ref={trackRef}
        data-slider-hitzone
        className="relative select-none cursor-ew-resize"
        style={{
          touchAction: 'none',
          paddingTop: TRACK_HIT_PAD_Y_PX,
          paddingBottom: TRACK_HIT_PAD_Y_PX,
        }}
        onPointerDown={onTrackPointerDown}
      >
        <div
          className="pointer-events-none relative h-[3px] w-full rounded-sm bg-[var(--ed-border-bright)]"
          aria-hidden
        >
          {ticks?.map((tv) => (
            <div
              key={tv}
              className="absolute top-1/2 h-1 w-px -translate-y-1/2 bg-[var(--ed-border)]"
              style={{ left: `${clampNum(toPct(tv), 0, 100)}%` }}
            />
          ))}
          {range ? (
            <div
              className="absolute top-0 h-full rounded-sm"
              style={{
                left: `${Math.min(pcts[0]!, pcts[1]!)}%`,
                width: `${Math.abs(pcts[1]! - pcts[0]!)}%`,
                background: color,
              }}
            />
          ) : (
            <div
              className="absolute top-0 h-full rounded-sm"
              style={{ left: 0, width: `${pcts[0] ?? 0}%`, background: color }}
            />
          )}
        </div>
        {thumbs.map((t, i) => (
          <button
            key={i}
            type="button"
            data-testid={t.testId}
            disabled={disabled}
            onPointerDown={onThumbPointerDown(i)}
            onKeyDown={onThumbKeyDown(i)}
            aria-label={t.ariaLabel ?? 'Drag to adjust'}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={t.value}
            tabIndex={disabled ? -1 : 0}
            className={
              'absolute top-1/2 h-[13px] w-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full ' +
              // Its halo is always there, so focus needs a mark of its own: a
              // keyboard user must see which thumb the arrows will move.
              'cursor-ew-resize transition-transform duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] '
              + `disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS_RING} ` +
              (activeIdx === i ? 'scale-110' : 'hover:scale-105')
            }
            style={{
              left: `${pcts[i]}%`,
              // Solid accent fill, matching `.ed-range`'s native-input thumb
              // (globals.css) and Chart.tsx's frequency-scrubber dot — every
              // other drag-a-value control in the app fills the thumb with
              // the accent color instead of leaving it a plain white dot, so
              // this primitive shouldn't be the odd one out.
              background: color,
              // The contact shadow is --rt-shadow, the recipe `.ed-range`'s
              // thumb uses: --rt-ink is light in the dark theme, where an ink
              // shadow would glow (AGENTS.md's Theme rule).
              boxShadow:
                `0 0 0 3px color-mix(in srgb, ${color} 25%, transparent), ` +
                '0 1px 3px color-mix(in srgb, var(--rt-shadow) 28%, transparent)',
            }}
          />
        ))}
      </div>
    </div>
  );
}
